import React, { useState, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { DashboardHeader } from './DashboardHeader';
//import { MobileNotSupported } from '../layout/MobileNotSupported';
import type { BreadcrumbItem, UserRole } from '../../types/navigation';
import { getNavigationConfig, filterNavigationByRole } from '../../config/navigation';
import { useAuthContext } from '../../contexts/AuthContext';
// Mobile detection is now handled by CSS in App.tsx
// import { useMobileDetection } from '../../hooks/useMobileDetection';

/**
 * DashboardLayout Component - Organism Level
 * 
 * Main layout for dashboard pages
 * 
 * Features:
 * - Collapsible sidebar
 * - Header with breadcrumbs and search
 * - Main content area
 * - Responsive design
 * - Role-based navigation
 */

export interface DashboardLayoutProps {
  /** Custom breadcrumbs */
  breadcrumbs?: BreadcrumbItem[];
  /** Search handler */
  onSearch?: (query: string) => void;
  /** Whether to show search bar */
  showSearch?: boolean;
  /** Children to render in main content area */
  children?: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  breadcrumbs,
  onSearch,
  showSearch = false,
  children,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useAuthContext();

  // Helper function to get user role from different profile types
  const getUserRole = (user: unknown): UserRole | undefined => {
    if (!user || typeof user !== 'object') return undefined;
    
    // Check if it's a User type (has role)
    if ('role' in user) {
      const userObj = user as { role?: string };
      return userObj.role as UserRole;
    }
    
    // For AdminProfile, use accessLevel
    if ('accessLevel' in user) {
      const userObj = user as { accessLevel?: string };
      return userObj.accessLevel as UserRole;
    }
    
    // For MerchantProfile, default to MERCHANT
    return 'MERCHANT' as UserRole;
  };

  // Filter navigation based on user role
  const sections = useMemo(() => {
    const navConfig = getNavigationConfig();
    const userRole = getUserRole(user);
    return filterNavigationByRole(navConfig, userRole);
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Sidebar */}
      <Sidebar
        sections={sections}
        defaultCollapsed={sidebarCollapsed}
        onCollapseChange={setSidebarCollapsed}
      />

      {/* Main Content Area */}
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'ml-28' : 'ml-72'
        }`}
      >
        {/* Header */}
        <DashboardHeader
          breadcrumbs={breadcrumbs}
          onSearch={onSearch}
          showSearch={showSearch}
        />

        {/* Main Content */}
        <main className="py-4">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;

